/**
 * Data-visualisation colour tokens.
 *
 * Dark and light are independently *selected*, not flipped: a light palette is
 * not a dark palette inverted, because contrast against the surface runs the
 * opposite direction. Every value here is verified by `chart.validate.test.ts`.
 *
 * The categorical ramp keeps the hue identity of `tokens.colors.inventory` for
 * six of seven slots. The seventh replaces the pale sage `#C3D7A4`, which reads
 * grey (chroma 0.072) and collapses into the green once brought into the
 * lightness band.
 */

export type ChartMode = 'dark' | 'light';
export type ChartMedia = 'screen' | 'export';

/** Slots are assigned in fixed order and never cycled. */
export const SERIES_SLOT_COUNT = 7;

/**
 * All-pairs separation is a strictly harder test than adjacent, and it binds a
 * series cap rather than a palette change — no ordering of seven hues can clear
 * it. Chart forms where any two marks can be neighbours (scatter, bubble, small
 * multiples) must not exceed this many series.
 */
export const ALL_PAIRS_SERIES_CAP = 3;

export interface ChartInk {
  /** Plot gridlines. Recessive. */
  grid: string;
  /** Axis lines and ticks. */
  axis: string;
  /** Axis and legend text. */
  label: string;
  tooltipBg: string;
  tooltipBorder: string;
  tooltipText: string;
  /** Fill opacity for area marks. */
  areaOpacity: number;
}

export interface ChartDiverging {
  negative: readonly string[];
  midpoint: string;
  positive: readonly string[];
}

export interface ChartTokenSet {
  surface: string;
  series: readonly string[];
  sequential: readonly string[];
  diverging: ChartDiverging;
  ink: ChartInk;
}

export const chartTokens: Record<ChartMode, ChartTokenSet> = {
  dark: {
    /**
     * KNOWN STALE. This is the pre-ladder `widget` value, and no surface in the
     * system is this colour any more — charts now draw on `widget` (#262626).
     *
     * It is pinned here rather than tracking the ladder because all three dark
     * palettes below are tuned against it and none survives the move: the
     * categorical adjacent-pair check fails, the sequential ramp's darkest step
     * `#00596a` drops to 1.90:1, and the diverging ramp's `#8d1d00` to 1.66:1,
     * against a 2:1 floor. Retuning three ramps is its own design exercise, so
     * it is tracked separately rather than bundled into the ladder change.
     *
     * The consequence while this stands: the dark chart palettes are certified
     * against a background they are not drawn on, and the darkest steps of the
     * sequential and diverging ramps are in practice below the mark floor.
     */
    surface: '#141414',
    // violet, orange, teal, red-orange, green, blue, yellow
    series: ['#6737fe', '#cd7a00', '#009ed2', '#fc4400', '#3a9c00', '#0058fb', '#9b9501'],
    // single-hue teal ramp, dark to light
    sequential: ['#00596a', '#00819c', '#00a9cf', '#22c9f2', '#7ce0fa'],
    diverging: {
      negative: ['#8d1d00', '#ab2600', '#ca3000'],
      midpoint: '#5e5e5e',
      positive: ['#00596a', '#00819c', '#00acd0'],
    },
    ink: {
      grid: 'rgba(255, 255, 255, 0.06)',
      axis: 'rgba(174, 174, 174, 0.55)',
      label: '#AEAEAE',
      tooltipBg: '#242424',
      tooltipBorder: '#2B2B33',
      tooltipText: '#FFFFFF',
      areaOpacity: 0.22,
    },
  },
  light: {
    surface: '#FFFFFF',
    series: ['#6107fe', '#ac6500', '#0084b0', '#d43800', '#2e8100', '#005cff', '#827d00'],
    // Pale to saturated: on white the low end must be the light one, and the
    // palest step still has to clear 2:1 against the surface to read as a mark.
    sequential: ['#00c4f0', '#00aed5', '#0098bb', '#0083a1', '#006e88'],
    diverging: {
      negative: ['#7a1c00', '#b32c00', '#ef4b12'],
      midpoint: '#888888',
      positive: ['#003d49', '#00728a', '#00acd0'],
    },
    ink: {
      grid: 'rgba(0, 0, 0, 0.08)',
      axis: 'rgba(92, 92, 92, 0.55)',
      label: '#5C5C5C',
      tooltipBg: '#FFFFFF',
      tooltipBorder: '#DDDDDD',
      tooltipText: '#1A1A1A',
      areaOpacity: 0.18,
    },
  },
};
